import { api } from './api';

export interface OrderTime {
  id: number;
  name: string;
  start_day: string;
  start_time: string;
  end_day: string;
  end_time: string;
}

export interface PickupSchedule {
  id: number;
  name: string;
  max_capacity: number;
}

export interface ParcelDayInfo {
  id: number;
  date: string;
  weekday: string;
  is_currently_open: boolean;
  status: 'open' | 'upcoming' | 'future';
  status_text: string;
  color_code: 'green' | 'orange' | 'red' | 'blue' | 'grey';
  order_time: OrderTime | null;
  pickup_schedule: PickupSchedule | null;
  notes: string | null;
}

export interface ParcelDayData {
  current_week: ParcelDayInfo[];
  next_week: ParcelDayInfo[];
  next_available_day: {
    id: number;
    date: string;
    weekday: string;
  } | null;
  can_place_orders: boolean;
}

export const parcelDaySelectionApi = {
  /**
   * Get available parcel days for the current week with Order Time information
   */
  getAvailableForOrder: async (): Promise<ParcelDayData> => {
    const response = await api.get<{ data: ParcelDayData }>('/v1/parcel-days/available-for-order');
    return response.data.data;
  },

  /**
   * Automatically assign the next available parcel day to an order
   */
  assignNextAvailableDay: async (): Promise<ParcelDayInfo | null> => {
    try {
      const data = await parcelDaySelectionApi.getAvailableForOrder();
      
      if (!data.can_place_orders || !data.next_available_day) {
        return null;
      }

      // Find the full parcel day info for the next available day
      const nextAvailableDay = data.current_week.find(
        day => day.id === data.next_available_day?.id
      ) || data.next_week.find(
        day => day.id === data.next_available_day?.id
      );

      return nextAvailableDay || null;
    } catch (error) {
      console.error('Error assigning next available parcel day:', error);
      return null;
    }
  },

  /**
   * Check if orders can be placed at the current time
   */
  canPlaceOrders: async (): Promise<boolean> => {
    try {
      const data = await parcelDaySelectionApi.getAvailableForOrder();
      return data.can_place_orders;
    } catch (error) {
      console.error('Error checking if orders can be placed:', error);
      return false;
    }
  },

  /**
   * Get the next available parcel day information
   */
  getNextAvailableDay: async (): Promise<ParcelDayInfo | null> => {
    try {
      const data = await parcelDaySelectionApi.getAvailableForOrder();
      
      if (!data.next_available_day) {
        return null;
      }

      // Find the full parcel day info for the next available day
      const nextAvailableDay = data.current_week.find(
        day => day.id === data.next_available_day?.id
      ) || data.next_week.find(
        day => day.id === data.next_available_day?.id
      );

      return nextAvailableDay || null;
    } catch (error) {
      console.error('Error getting next available parcel day:', error);
      return null;
    }
  }
}; 